import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Image,
  message,
  Pagination,
  Popconfirm,
  Space,
  Table,
} from 'antd';
import type { TableColumnType } from 'antd';
import {
  getBannerList,
  batchOfflineBanner,
  BannerItem,
  GetBannerListParams,
} from '@/api/banner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import dayjs from 'dayjs';
import { PageLink } from '@/config/pageConfig';
import { useRequest, useUpdateEffect } from 'ahooks';

const BannerMng: React.FC = () => {
  const [form] =
    Form.useForm<Omit<GetBannerListParams, 'currentPage' | 'pageSize'>>();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const isMobile = useAtomValue(isMobileAtom);
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const formValues: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      formValues[key] = value;
    });
    form.setFieldsValue(formValues);
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
  }, [searchParams, form]);

  const { data, loading, refresh, run } = useRequest(async (page = 1) => {
    const params: GetBannerListParams = {
      currentPage: page,
      pageSize,
    };
    const resp = await getBannerList(params);

    if (resp.status === 200 && resp.data.success) {
      return resp.data.data;
    }

    return {
      records: [],
      total: 0,
      currentPage: page,
      pageSize,
    };
  });

  useUpdateEffect(() => {
    run(page);
  }, [page]);

  const columns: TableColumnType<BannerItem>[] = [
    {
      title: 'id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '推送图预览',
      dataIndex: 'image',
      key: 'image',
      render: (src: string) => (
        <Image
          src={src}
          width={50}
          height={50}
          style={{ objectFit: 'cover', borderRadius: '4px' }}
          placeholder={
            <div
              style={{
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <span style={{ fontSize: '12px', color: '#999' }}>Loading</span>
            </div>
          }
          preview={{ src }}
        />
      ),
    },
    {
      title: '推送状态',
      dataIndex: 'status',
      key: 'status',
      render: (value: number) => (value === 1 ? '上线' : '下线'),
    },
    {
      title: '发布时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '推送链接',
      dataIndex: 'jumpContent',
      key: 'jumpContent',
      render: (value: string) => value || '-',
    },
    {
      title: '操作人员',
      dataIndex: 'updateUser',
      key: 'updateUser',
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            onClick={() =>
              navigate(`${PageLink.BannerMng}/detail/${record.id}`)
            }
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  const batchOfflineDisabled = useMemo(() => {
    return !selectedKeys.length;
  }, [selectedKeys]);

  const handleBatchOffline = useCallback(async () => {
    const res = await batchOfflineBanner({
      idList: selectedKeys.map(Number),
    });

    if (res.status !== 200 || !res.data.success) {
      return message.error(res.data.message || '操作失败');
    }

    message.success('下线成功');
    refresh();
    setSelectedKeys([]);
  }, [selectedKeys, refresh]);

  return (
    <div className="content-review-container">
      {contextHolder}
      <div className="table-container">
        <Space size="middle" className="table-header">
          <Button
            type="primary"
            onClick={() => navigate(`${PageLink.BannerMng}/create`)}
          >
            创建
          </Button>
          <Popconfirm
            title="你确定要将这些内容下线吗?"
            onConfirm={handleBatchOffline}
            okType="danger"
          >
            <Button type="primary" danger disabled={batchOfflineDisabled}>
              批量下线
            </Button>
          </Popconfirm>
        </Space>
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          key="banner"
          columns={columns}
          dataSource={data?.records || []}
          scroll={{ x: 'max-content' }}
          rowSelection={
            isMobile
              ? undefined
              : {
                  selectedRowKeys: selectedKeys,
                  type: 'checkbox',
                  fixed: true,
                  onChange: (selectedRowKeys) => {
                    setSelectedKeys(selectedRowKeys as string[]);
                  },
                  getCheckboxProps: (record) => ({
                    disabled: record.status !== 1,
                  }),
                }
          }
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>
    </div>
  );
};

export default BannerMng;
